import { useContext, useEffect, useRef, useState } from "react";
import ReactLoading from "react-loading";
import { OutorgaContext } from "../../../../../contexts/Outorga";
import { getGrantChartsParameters } from "../../../../../services/outorga";
import ChartsRow from "./Charts/ChartsRow";

const Charts = ({ grantId, permissions, isMasterOrAdmin }) => {
    const context = useContext(OutorgaContext);

    const [isInitializing, setIsInitializing] = useState(false);
    const [paramsList, setParamsList] = useState([]);
    const [chartsList, setChartsList] = useState([]);

    const {
        grantDetails,
        isLoadingGrantCharts,
        handleGetGrantCharts,
        handleDeleteGrantCharts,
        handleSaveGrantCharts,
        setUnsavedCharts
    } = context;

    const canEdit = isMasterOrAdmin || permissions?.patch || permissions?.post;
    const canDelete = isMasterOrAdmin || permissions?.delete;

    const getParametersList = async () => {
        const resp = await getGrantChartsParameters(grantId);
        return resp?.data;
    };

    useEffect(() => {
        const init = async () => {
            const params = await getParametersList();

            handleGetGrantCharts(grantId);

            setParamsList(
                params?.map(param => ({
                    value: param.id,
                    label: `${param.nome} (${param.tipo_grafico})`,
                    type: param.tipo_grafico,
                    ativo: param.ativo
                })) || []
            );
        };

        init();
    }, [grantId]);

    useEffect(() => {
        if (!grantDetails?.charts || paramsList.length === 0) return;

        setChartsList(
            grantDetails.charts.map(chart => ({
                id: chart.id,
                parameter: paramsList.find(param => param.value === chart.parametro_principal),
                parametersMerge: paramsList.filter(param =>
                    chart.parametros_mesclados.includes(param.value)
                )
            }))
        );
    }, [grantDetails?.charts, paramsList]);

    useEffect(() => {
        const payload = chartsList.map(chart => ({
            id: chart.id,
            parametro_principal: chart?.parameter?.value,
            parametros_mesclados: chart?.parametersMerge?.map(param => param.value)
        }));

        setUnsavedCharts(payload);
    }, [chartsList]);

    const handleAdd = (parameter) => {
        const newChart = {
            isNew: parameter.isNew,
            parameter: parameter.parameter,
            parametersMerge: parameter.parametersMerge,
            key: parameter.id
        };

        setChartsList(prev => [...prev, newChart]);
    };

    const handleRemove = (item) => {
        if (item.isNew) {

            setChartsList(prev => prev.filter(chart => chart.key !== item.key));

            return;
        }

        handleDeleteGrantCharts(grantId, item.id);
    };

    const handleParameterChange = (id, param) => {
        setChartsList(prev =>
            prev.map(chart =>
                chart.id === id
                    ? { ...chart, parameter: param }
                    : chart
            )
        );
    };

    const handleMergeChange = (id, merge) => {
        setChartsList(prev =>
            prev.map(chart =>
                chart.id === id
                    ? { ...chart, parametersMerge: merge }
                    : chart
            )
        );
    };

    const getAvailableParams = (currentChartId = null) => {
        const usedParams = chartsList
            .filter(chart => chart.id !== currentChartId)
            .map(chart => chart.parameter?.value)
            .filter(Boolean);

        return paramsList
            .filter(param => !usedParams.includes(param.value))
            .filter(param => currentChartId || param.ativo);
    };

    const handleSave = () => {
        const payload = {
            graficos: chartsList.map(chart => ({
                id: chart.id,
                parametro_principal_id: chart.parameter.value,
                parametros_mesclados: chart?.parametersMerge?.map(param => param.value)
            }))
        };

        handleSaveGrantCharts(grantId, payload);
    };

    return (
        <div className="grant-charts">
            {(isInitializing || isLoadingGrantCharts) && (
                <div className="grant-charts__loading">
                    <ReactLoading
                        type="spin"
                        color="var(--background-menu)"
                        width={40}
                    />
                </div>
            )}

            <h4>Gráficos da Outorga</h4>

            <div className="grant-charts__body">
                <ChartsRow
                    isNew
                    paramsList={getAvailableParams()}
                    fullParamsList={paramsList.filter(param => param.ativo)}
                    onAdd={handleAdd}
                />
            </div>

            <hr />

            <div className="grant-charts__list">
                {chartsList.map(item => (
                    <ChartsRow
                        key={item.id}
                        data={item}
                        paramsList={getAvailableParams(item.id)}
                        fullParamsList={paramsList.filter(param => param.ativo)}
                        onRemove={() => handleRemove(item)}
                        onParameterChange={handleParameterChange}
                        onMergeChange={handleMergeChange}
                        canDelete={canDelete}
                    />
                ))}
            </div>

            {(chartsList.length > 0 && canEdit) && (
                <div>
                    <hr />
                    <div className="grant-charts__save-btn">
                        <button
                            onClick={handleSave}
                            className="hdv-btn-forms hdv-btn-green"
                        >
                            Salvar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Charts;